<!-- Modern Enterprise Waiter Management -->
<div class="waiter-management-container">
  <!-- Page Header -->
  <div class="page-header">
    <div class="header-content">
      <div class="header-main">
        <div class="header-icon">
          <i class="fas fa-user-tie"></i>
        </div>
        <div class="header-text">
          <h1 class="page-title">Waiter Management</h1>
          <p class="page-description">Monitor and manage waiter accounts, status, and balances</p>
        </div>
      </div>
      <div class="header-actions">
        <button class="btn btn-outline-secondary" (click)="getUsers()">
          <i class="fas fa-sync-alt me-1"></i>
          Refresh
        </button>
      </div>
    </div>
  </div>

  <!-- Main Content -->
  <div class="content-container">
    <!-- Filters and Search Panel -->
    <div class="filters-panel">
      <div class="search-section">
        <div class="search-box">
          <div class="search-input-wrapper">
            <i class="fas fa-search search-icon"></i>
            <input type="text"
                   class="search-input"
                   placeholder="Search by name, email, or phone..."
                   [(ngModel)]="search"
                   (input)="onSearch()">
            @if (search) {
              <button class="clear-search"
                      (click)="search = ''; onSearch()">
                <i class="fas fa-times"></i>
              </button>
            }
          </div>
        </div>
      </div>

      <div class="filters-section">
        <div class="filter-group">
          <div ngbDropdown class="filter-dropdown">
            <button type="button"
                    class="btn btn-filter"
                    id="dropdownBasic1"
                    ngbDropdownToggle>
              <i class="fas fa-circle me-2"></i>
              {{ capitalizeFirstLetter(selectedStatus) || 'All Status' }}
              <i class="fas fa-chevron-down ms-2"></i>
            </button>
            <div ngbDropdownMenu aria-labelledby="dropdownBasic1" class="filter-menu">
              <button ngbDropdownItem (click)="clearFilter()" class="filter-item">
                <i class="fas fa-circle me-2"></i>All Status
              </button>
              <button ngbDropdownItem (click)="statuusFilter('active')" class="filter-item">
                <i class="fas fa-check-circle me-2 text-success"></i>Active
              </button>
              <button ngbDropdownItem (click)="statuusFilter('inactive')" class="filter-item">
                <i class="fas fa-times-circle me-2 text-danger"></i>Suspended
              </button>
            </div>
          </div>

          @if (selectedStatus || search) {
            <button class="btn btn-clear-filters"
                    (click)="clearFilter()">
              <i class="fas fa-times me-1"></i>
              Clear Filters
            </button>
          }
        </div>
      </div>

      <div class="stats-section">
        <div class="stat-card">
          <div class="stat-icon">
            <i class="fas fa-user-tie"></i>
          </div>
          <div class="stat-content">
            <div class="stat-number">{{ users?.length || 0 }}</div>
            <div class="stat-label">Waiters Found</div>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon total">
            <i class="fas fa-database"></i>
          </div>
          <div class="stat-content">
            <div class="stat-number">{{ size || 0 }}</div>
            <div class="stat-label">Total Waiters</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Waiters Table Panel -->
    <div class="data-panel">
      <div class="panel-header">
        <div class="panel-title">
          <div class="title-icon">
            <i class="fas fa-table"></i>
          </div>
          <div class="title-content">
            <div class="title-text">Waiters Directory</div>
            <div class="title-subtitle">Complete list of waiters with their roles and status</div>
          </div>
        </div>
      </div>

      <div class="panel-body">
        <div class="table-container">
          <table class="table align-middle">
            <thead>
              <tr>
                <th>
                  <div class="th-content">
                    <i class="fas fa-user me-2"></i>
                    Waiter
                  </div>
                </th>
                <th>
                  <div class="th-content">
                    <i class="fas fa-envelope me-2"></i>
                    Contact Info
                  </div>
                </th>
                <th>
                  <div class="th-content">
                    <i class="fas fa-user-tag me-2"></i>
                    Role
                  </div>
                </th>
                <th>
                  <div class="th-content">
                    <i class="fas fa-circle me-2"></i>
                    Status
                  </div>
                </th>
                <th>
                  <div class="th-content">
                    <i class="fas fa-cog me-2"></i>
                    Actions
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              @for(user of users; track user.id) {
                <tr class="waiter-row">
                  <td>
                    <div class="user-info">
                      <div class="user-avatar">
                        <img [src]="user?.image || 'profile.png'"
                             [alt]="user?.first_name + ' ' + user?.last_name"
                             class="avatar-img">
                        <div class="avatar-status"
                             [class.status-active]="user?.disabled == false"
                             [class.status-inactive]="user?.disabled != false"></div>
                      </div>
                      <div class="user-details">
                        <div class="user-name">{{ user?.first_name }} {{ user?.last_name }}</div>
                        <div class="user-id">ID: {{ user?.id }}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div class="contact-info">
                      <div class="contact-item">
                        <i class="fas fa-envelope contact-icon"></i>
                        <span class="contact-text">{{ user?.email }}</span>
                      </div>
                      <div class="contact-item">
                        <i class="fas fa-phone contact-icon"></i>
                        <span class="contact-text">{{ user?.phone || 'N/A' }}</span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div class="roles-container">
                      @for(role of user.roles; track role; let last = $last) {
                        <span class="badge badge-soft-primary badge-dot">
                          {{ role }}@if(user.roles.length > 1 && !last){,}
                        </span>
                      }
                    </div>
                  </td>
                  <td>
                    @if (user?.disabled == false) {
                      <span class="badge badge-dot badge-status-active">
                        Active
                      </span>
                    } @else {
                      <span class="badge badge-dot badge-status-suspended">
                        Suspended
                      </span>
                    }
                  </td>
                  <td>
                    <div ngbDropdown container="body" display="dynamic" class="action-dropdown">
                      <button type="button"
                              class="btn btn-action"
                              id="dropdownBasic1"
                              ngbDropdownToggle
                              aria-label="Waiter actions"
                              title="More actions">
                        <i class="fas fa-ellipsis-v"></i>
                      </button>
                      <div ngbDropdownMenu aria-labelledby="dropdownBasic1" class="action-menu">
                        <button ngbDropdownItem (click)="updateStatus(user?.id)" class="action-item">
                          @if (user?.disabled == false) {
                            <i class="fas fa-ban me-2 text-danger"></i>
                            <span class="text-danger">Suspend Waiter</span>
                          } @else {
                            <i class="fas fa-check-circle me-2 text-success"></i>
                            <span class="text-success">Activate Waiter</span>
                          }
                        </button>
                        <div class="dropdown-divider"></div>
                        <button ngbDropdownItem (click)="edit(user?.id)" class="action-item">
                          <i class="fas fa-wallet me-2"></i>
                          View Balance
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
              } @empty {
                <tr>
                  <td colspan="5">
                    <div class="empty-state">
                      @if (search || selectedStatus) {
                        <div class="empty-icon">
                          <i class="fas fa-search fa-3x"></i>
                        </div>
                        <div class="empty-content">
                          <h5>No Waiters Found</h5>
                          <p>No waiters match your current search criteria. Try adjusting your filters or search terms.</p>
                          <button class="btn btn-outline-primary" (click)="clearFilter()">
                            Clear All Filters
                          </button>
                        </div>
                      } @else {
                        <div class="empty-icon">
                          <i class="fas fa-user-tie fa-3x"></i>
                        </div>
                        <div class="empty-content">
                          <h5>No Waiters Yet</h5>
                          <p>There are no waiters registered at the moment.</p>
                        </div>
                      }
                    </div>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </div>

      <!-- Pagination Footer -->
      @if (size > pageSize) {
        <div class="panel-footer">
          <div class="pagination-container">
            <div class="pagination-info">
              <span>Showing page {{ page }} of {{ (size / pageSize) | number:'1.0-0' }}</span>
            </div>
            <ngb-pagination
              [collectionSize]="size"
              [(page)]="page"
              [pageSize]="pageSize"
              [maxSize]="5"
              [rotate]="true"
              [ellipses]="false"
              [boundaryLinks]="true"
              (pageChange)="refresh($event)"
              class="modern-pagination">
            </ngb-pagination>
          </div>
        </div>
      }
    </div>
  </div>
</div>
